import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Heading, Text, Button } from '../styles/components';

const ResultWrapper = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  max-width: 600px;
  margin: 0 auto;
`;

const ResultIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ResultMessage = styled(Text)`
  white-space: pre-line;
  margin: ${props => props.theme.spacing.lg} 0;
  text-align: left;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
`;

const CalendlyEmbed = styled.div`
  margin-top: ${props => props.theme.spacing.xl};
  min-height: 700px;
  width: 100%;
  
  .calendly-inline-widget {
    min-width: 320px;
    height: 700px;
  }
`;

const WaitlistPrompt = styled.div`
  text-align: center;
  margin: ${props => props.theme.spacing.xl} 0;
`;

const BetaApplicationResult = ({ result, message, metadata, onClose, email }) => {
  const [showWaitlistPrompt, setShowWaitlistPrompt] = useState(false);
  
  const handleWaitlistResponse = async (joinWaitlist) => {
    try {
      // Update tags in ConvertKit
      await fetch('/api/update-tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          addTags: joinWaitlist ? ['waitlist-accepted'] : ['waitlist-declined']
        }),
      });
      
      onClose();
    } catch (error) {
      console.error('Error updating waitlist status:', error);
    }
  };

  useEffect(() => {
    // Load Calendly widget script
    if (result === 'CALENDLY') {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [result]);

  const getIcon = () => {
    switch (result) {
      case 'CALENDLY':
        return '🎉';
      case 'WAITLIST':
        return '📝';
      case 'REVIEW':
        return '🤔';
      default:
        return '✨';
    }
  };

  const getTitle = () => {
    switch (result) {
      case 'CALENDLY':
        return 'Welcome to the Beta Program!';
      case 'WAITLIST':
        return 'Join Our Waitlist';
      case 'REVIEW':
        return 'Application Under Review';
      default:
        return 'Thank You!';
    }
  };

  const renderActionButtons = () => {
    switch (result) {
      case 'CALENDLY':
        return (
          <>
            <CalendlyEmbed>
              <div 
                className="calendly-inline-widget" 
                data-url={process.env.REACT_APP_CALENDLY_URL_EUROPE}
                style={{ minWidth: '320px', height: '700px' }}
              />
            </CalendlyEmbed>
            <ButtonGroup>
              <Button onClick={onClose}>Close</Button>
            </ButtonGroup>
          </>
        );
      
      case 'WAITLIST':
      case 'REVIEW':
        return showWaitlistPrompt ? (
          <WaitlistPrompt>
            <Text size="large" $mb={props => props.theme.spacing.lg}>
              Would you like to join our waitlist?
            </Text>
            <ButtonGroup>
              <Button onClick={() => handleWaitlistResponse(true)}>
                Yes, keep me updated
              </Button>
              <Button $secondary onClick={() => handleWaitlistResponse(false)}>
                No, thanks
              </Button>
            </ButtonGroup>
          </WaitlistPrompt>
        ) : (
          <ButtonGroup>
            <Button onClick={() => setShowWaitlistPrompt(true)}>
              Join Waitlist
            </Button>
            <Button $secondary onClick={onClose}>
              Close
            </Button>
          </ButtonGroup>
        );
      
      default:
        return (
          <ButtonGroup>
            <Button onClick={onClose}>Close</Button>
          </ButtonGroup>
        );
    }
  };

  return (
    <ResultWrapper>
      <ResultIcon>{getIcon()}</ResultIcon>
      <Heading as="h2" $mb={props => props.theme.spacing.lg}>
        {getTitle()}
      </Heading>
      <ResultMessage>
        {message}
      </ResultMessage>
      {metadata?.keyFactors && (
        <Text size="small" color="textLight" style={{ marginTop: '1rem' }}>
          Key factors: {metadata.keyFactors.join(', ')}
        </Text>
      )}
      {renderActionButtons()}
    </ResultWrapper>
  );
};

export default BetaApplicationResult; 